import React, { useState } from 'react';
import { generateHashRemovalEmail } from '../../lib/emails/hashRemovalEmail';
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface PaymentRecord {
  id: string;
  user_id: number;
  amount: number;
  shares_to_purchase: number;
  status: string;
  network: string;
  transaction_hash: string;
  created_at: string;
  user?: {
    id: number;
    username: string;
    email: string;
    full_name: string | null;
  };
}

interface HashRemovalManagerProps {
  adminUser: any;
}

const HashRemovalManager: React.FC<HashRemovalManagerProps> = ({ adminUser }) => {
  const [hashInput, setHashInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [foundPayment, setFoundPayment] = useState<PaymentRecord | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const searchHash = async () => {
    if (!hashInput.trim()) {
      setMessage({ type: 'error', text: 'Please enter a transaction hash' });
      return;
    }

    setLoading(true);
    setMessage(null);
    setFoundPayment(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/hash-removal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'search_hash',
          data: { hash: hashInput.trim() }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      if (!result.data) {
        setMessage({ type: 'error', text: 'Hash not found in database' });
        return;
      }

      setFoundPayment(result.data as PaymentRecord);
      setShowConfirmDialog(true);
    } catch (error) {
      console.error('❌ Error searching for hash:', error);
      setMessage({ type: 'error', text: 'Failed to search for hash. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const deleteHash = async () => {
    if (!foundPayment || !adminUser?.database_user?.id) {
      setMessage({ type: 'error', text: 'Missing required data for deletion' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      console.log('🗑️ Deleting hash:', {
        payment_id: foundPayment.id,
        hash: foundPayment.transaction_hash,
        user: foundPayment.user?.username,
        admin: adminUser.email
      });

      // Delete hash via API (handles audit trail and deletion)
      const response = await fetch(`${API_BASE_URL}/api/admin/hash-removal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'delete_hash',
          data: {
            payment: foundPayment,
            adminUserId: adminUser.database_user.id,
            adminEmail: adminUser.email
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      console.log('✅ Payment record deleted');

      // Step 3: Send email notification
      let emailSuccess = false;
      try {
        const emailData = generateHashRemovalEmail({
          userName: foundPayment.user?.full_name || foundPayment.user?.username || 'User',
          userEmail: foundPayment.user?.email || '',
          amount: parseFloat(foundPayment.amount.toString()),
          shares: parseFloat(foundPayment.shares_to_purchase.toString()),
          network: foundPayment.network,
          deletedHash: foundPayment.transaction_hash
        });

        const emailResponse = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({
            emailType: 'hash_removal',
            data: {
              to: foundPayment.user?.email,
              subject: emailData.subject,
              html: emailData.html,
              text: emailData.text
            }
          })
        });

        const emailResult = await emailResponse.json();
        emailSuccess = emailResult.success;

        if (emailSuccess) {
          console.log('✅ Email notification sent to:', foundPayment.user?.email);
        } else {
          console.warn('⚠️ Email notification failed:', emailResult.error);
        }
      } catch (emailError) {
        console.error('❌ Error sending email:', emailError);
      }

      // Success message
      const successMsg = emailSuccess
        ? 'Hash deleted successfully. User has been notified via email.'
        : 'Hash deleted but email notification failed. Please contact the user manually.';

      setMessage({ type: emailSuccess ? 'success' : 'info', text: successMsg });
      setHashInput('');
      setFoundPayment(null);
      setShowConfirmDialog(false);

      console.log('✅ Hash removal complete:', {
        admin: adminUser.email,
        deleted_hash: foundPayment.transaction_hash,
        user_email: foundPayment.user?.email,
        email_sent: emailSuccess
      });
    } catch (error) {
      console.error('❌ Error deleting hash:', error);
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to delete hash. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
    setFoundPayment(null);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-2xl font-bold text-white mb-4">Remove Transaction Hash</h2>
      <p className="text-gray-300 mb-6">
        Remove a crypto payment transaction hash to allow users to resubmit with a different hash.
      </p>

      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-900/50 border border-green-500 text-green-200' :
          message.type === 'error' ? 'bg-red-900/50 border border-red-500 text-red-200' :
          'bg-blue-900/50 border border-blue-500 text-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Search Section */}
      {!showConfirmDialog && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Transaction Hash
            </label>
            <input
              type="text"
              value={hashInput}
              onChange={(e) => setHashInput(e.target.value)}
              placeholder="Enter transaction hash to search..."
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          <button
            onClick={searchHash}
            disabled={loading || !hashInput.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {loading ? 'Searching...' : 'Search & Delete Hash'}
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && foundPayment && (
        <div className="bg-gray-700 rounded-lg p-6 border-2 border-yellow-500">
          <h3 className="text-xl font-bold text-yellow-400 mb-4">⚠️ Confirm Hash Deletion</h3>
          
          {/* Payment Details */}
          <div className="bg-gray-800 rounded-lg p-4 mb-4">
            <h4 className="text-lg font-semibold text-white mb-3">Payment Details</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white">User:</span>
                <span className="text-white font-medium">{foundPayment.user?.username}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Email:</span>
                <span className="text-white font-medium">{foundPayment.user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Full Name:</span>
                <span className="text-white font-medium">{foundPayment.user?.full_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Amount:</span>
                <span className="text-white font-medium">${parseFloat(foundPayment.amount.toString()).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Shares:</span>
                <span className="text-white font-medium">{parseFloat(foundPayment.shares_to_purchase.toString())}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Status:</span>
                <span className={`font-medium ${
                  foundPayment.status === 'approved' ? 'text-green-400' :
                  foundPayment.status === 'rejected' ? 'text-red-400' :
                  'text-yellow-400'
                }`}>
                  {foundPayment.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Network:</span>
                <span className="text-white font-medium">{foundPayment.network}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white">Created:</span>
                <span className="text-white font-medium">{new Date(foundPayment.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-900/30 border border-yellow-500 rounded-lg p-4 mb-4">
            <p className="text-yellow-200 text-sm">
              <strong>Warning:</strong> This will permanently delete the transaction hash from the database and allow the user to resubmit a new transaction hash. The user will be notified via email.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={deleteHash}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed font-bold transition-colors"
            >
              {loading ? 'Deleting...' : 'Yes, Delete Hash'}
            </button>
            <button
              onClick={cancelDelete}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-500 disabled:cursor-not-allowed font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HashRemovalManager;

